// pages/OrdersPage.tsx
import { useState, useMemo } from "react";
import { Search, Filter, X, Eye, CreditCard, Truck, CheckCircle, XCircle, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, useOrders } from "@cabukaka/hooks";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import OrdersList from "../../../components/features/orders/OrdersList";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Order } from "@cabukaka/types";

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const { orders, deleteOrder, updateOrderStatus } = useOrders();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredOrders = useMemo(() => {
    let result = orders;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(o =>
        o.reference?.toLowerCase().includes(searchLower) ||
        o.client?.name?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus) {
      result = result.filter(o => o.status === selectedStatus);
    }

    if (selectedPaymentStatus) {
      result = result.filter(o => o.paymentStatus === selectedPaymentStatus);
    }

    return result;
  }, [orders, searchTerm, selectedStatus, selectedPaymentStatus]);

  const stats = useMemo(() => ({
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    delivered: orders.filter(o => o.status === "delivered").length,
    cancelled: orders.filter(o => o.status === "cancelled").length,
    totalRevenue: orders.filter(o => o.status === "delivered").reduce((sum, o) => sum + o.total, 0)
  }), [orders]);

  const handleDelete = async () => {
    if (!orderToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteOrder(orderToDelete.id);
      setOrderToDelete(null);
      alertSuccess("Commande supprimée avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateStatus = async (orderId: string, status: string) => {
    try {
      await updateOrderStatus(orderId, status);
      const statusMessages = {
        preparing: "Préparation de la commande",
        shipped: "Commande expédiée",
        delivered: "Commande livrée",
        cancelled: "Commande annulée"
      };
      alertSuccess(statusMessages[status as keyof typeof statusMessages] || "Statut mis à jour");
    } catch (err) {
      alertError("Erreur lors de la mise à jour");
    }
  };

  const clearFilters = () => {
    setSelectedStatus("");
    setSelectedPaymentStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedStatus || selectedPaymentStatus || searchTerm;

  const statusOptions = [
    { value: "pending", label: "En attente" },
    { value: "confirmed", label: "Confirmée" },
    { value: "preparing", label: "Préparation" },
    { value: "shipped", label: "Expédiée" },
    { value: "delivered", label: "Livrée" },
    { value: "cancelled", label: "Annulée" }
  ];

  const paymentStatusOptions = [
    { value: "pending", label: "En attente" },
    { value: "paid", label: "Payé" },
    { value: "failed", label: "Échoué" },
    { value: "refunded", label: "Remboursé" }
  ];

  return (
    <PageLayout
      title="Gestion des commandes"
      description={`${filteredOrders.length} commande${filteredOrders.length > 1 ? 's' : ''} affiché${filteredOrders.length > 1 ? 's' : ''}`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-700/70">Total</p>
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-yellow-700/70">En attente</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-green-700/70">Livrées</p>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-red-700/70">Annulées</p>
          <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-emerald-700/70">CA</p>
          <p className="text-lg font-bold text-emerald-700">{stats.totalRevenue.toLocaleString('fr-FR')} FCFA</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par référence ou client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut commande</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tous les statuts</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut paiement</label>
              <select
                value={selectedPaymentStatus}
                onChange={(e) => setSelectedPaymentStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tous les statuts</option>
                {paymentStatusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <OrdersList
        orders={filteredOrders}
        selectable={role === "admin"}
        onAction={(o) => setSelectedOrder(o)}
        selectActions={role === "admin" ? [{
          label: "Supprimer",
          onClick: (selected) => setOrderToDelete(selected[0]),
          className: "bg-red-600 text-white"
        }] : undefined}
      />

      {/* Menu modal */}
      {/* Menu modal */}
      {/* Menu modal */}
      {selectedOrder && (
        <MenuModal
          title={`Commande ${selectedOrder.reference}`}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          icon={<CreditCard className="text-primary" size={20} />}
          menu={[
            { label: "Voir détails", icon: Eye, onClick: () => navigate(`/${role}/orders/details?id=${selectedOrder.id}`) },
            ...((role === "admin" || role === "shop") ? [
              ...(selectedOrder.status !== "preparing" && selectedOrder.status !== "shipped" && selectedOrder.status !== "delivered" && selectedOrder.status !== "cancelled" ?
                [{ label: "Préparer la commande", icon: Package, onClick: () => handleUpdateStatus(selectedOrder.id, "preparing") }] : []),
              ...(selectedOrder.status === "preparing" ?
                [{ label: "Expédier la commande", icon: Truck, onClick: () => handleUpdateStatus(selectedOrder.id, "shipped") }] : []),
              ...(selectedOrder.status === "shipped" ?
                [{ label: "Livrer la commande", icon: CheckCircle, onClick: () => handleUpdateStatus(selectedOrder.id, "delivered") }] : []),
              ...(selectedOrder.status !== "cancelled" && selectedOrder.status !== "delivered" ?
                [{ label: "Annuler la commande", icon: XCircle, onClick: () => handleUpdateStatus(selectedOrder.id, "cancelled") }] : [])
            ] : []),
            { label: "Supprimer", icon: CreditCard, onClick: () => { setOrderToDelete(selectedOrder); setSelectedOrder(null); } }
          ]}
        />
      )}

      {/* Modal suppression */}
      <DeleteConfirmationModal
        isOpen={!!orderToDelete}
        onClose={() => setOrderToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la commande"
        message={`Supprimer la commande "${orderToDelete?.reference}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
    </PageLayout>
  );
}